// src/PushNotification.js
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiBell, FiCheck, FiX } from "react-icons/fi";
import { messaging } from "./firebase";
import { getToken, onMessage } from "firebase/messaging";
import { toast } from "react-toastify";

const PushNotification = () => {
  const [permission, setPermission] = useState("default");
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // ─── Colors ─────────────────────────────────────────────
  const c = {
    bg: "rgba(10, 10, 15, 0.95)",
    bgLight: "rgba(15, 15, 25, 0.9)",
    border: "rgba(255, 255, 255, 0.08)",
    text: "#f0f0f5",
    textMuted: "#8888a0",
    primary: "#6366f1",
    primaryLight: "rgba(99, 102, 241, 0.1)",
    primaryBorder: "rgba(99, 102, 241, 0.2)",
    green: "#22c55e",
    greenLight: "rgba(34, 197, 94, 0.1)",
    greenBorder: "rgba(34, 197, 94, 0.2)",
    red: "#f43f5e",
    redLight: "rgba(244, 63, 94, 0.1)",
    redBorder: "rgba(244, 63, 94, 0.2)",
    gradient: "linear-gradient(135deg, #6366f1, #a855f7)",
  };

  useEffect(() => {
    // Check current permission status
    if ("Notification" in window) {
      setPermission(Notification.permission);

      // Show banner if not yet asked
      if (Notification.permission === "default" && !dismissed) {
        const timer = setTimeout(() => setShowBanner(true), 3000);
        return () => clearTimeout(timer);
      }
    }

    // Request permission and setup
    const requestPermission = async () => {
      if (Notification.permission === "granted") {
        setupMessaging();
      }
    };

    requestPermission();

    // Foreground message handler
    if (messaging) {
      onMessage(messaging, (payload) => {
        console.log("Foreground message received:", payload);
        toast.info(
          `${payload.notification.title}: ${payload.notification.body}`,
          {
            icon: "🔔",
            style: {
              background: c.bg,
              border: `1px solid ${c.border}`,
              color: c.text,
            },
          }
        );
      });
    }
  }, []);

  const setupMessaging = async () => {
    try {
      const currentToken = await getToken(messaging, {
        vapidKey: "BH4i3qKReSOY9cuXqZ13N7s_jqO70EdTpDevx5e60pu0X3hqYqkBRtrPLPhTBuesCjyULY3Sb_YQRzrOa2WyRWw",
      });

      if (currentToken) {
        console.log("FCM Token:", currentToken);
        setPermission("granted");
      } else {
        console.log("No registration token available.");
      }
    } catch (err) {
      console.error("Error retrieving token:", err);
    }
  };

  const handleAllow = async () => {
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      setShowBanner(false);

      if (result === "granted") {
        setupMessaging();
        toast.success("Notifications enabled! 🔔", {
          style: {
            background: c.bg,
            border: `1px solid ${c.greenBorder}`,
            color: c.text,
          },
        });
      }
    } catch (err) {
      console.error("Permission error:", err);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
  };

  // ─── Styles ─────────────────────────────────────────────
  const styles = {
    // Notification permission banner
    banner: {
      position: "fixed",
      bottom: "100px",
      left: "50%",
      transform: "translateX(-50%)",
      width: "calc(100% - 32px)",
      maxWidth: "400px",
      background: c.bg,
      border: `1px solid ${c.border}`,
      borderRadius: "20px",
      padding: "20px",
      zIndex: 9997,
      backdropFilter: "blur(24px)",
      boxShadow: "0 16px 48px rgba(0, 0, 0, 0.5)",
    },

    bannerContent: {
      display: "flex",
      flexDirection: "column",
      gap: "16px",
    },

    bannerHeader: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },

    bannerIcon: {
      width: "44px",
      height: "44px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: c.primaryLight,
      border: `1px solid ${c.primaryBorder}`,
      borderRadius: "12px",
      color: c.primary,
      fontSize: "1.2rem",
      flexShrink: 0,
    },

    bannerText: {
      flex: 1,
    },

    bannerTitle: {
      fontSize: "0.95rem",
      fontWeight: "700",
      color: c.text,
      marginBottom: "2px",
    },

    bannerDesc: {
      fontSize: "0.8rem",
      color: c.textMuted,
      lineHeight: "1.5",
    },

    bannerActions: {
      display: "flex",
      gap: "10px",
    },

    allowBtn: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: "10px 16px",
      background: c.gradient,
      border: "none",
      borderRadius: "10px",
      color: "white",
      fontSize: "0.85rem",
      fontWeight: "600",
      fontFamily: "inherit",
      cursor: "pointer",
      boxShadow: "0 4px 15px rgba(99, 102, 241, 0.3)",
    },

    dismissBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "10px 16px",
      background: "rgba(255, 255, 255, 0.05)",
      border: `1px solid ${c.border}`,
      borderRadius: "10px",
      color: c.textMuted,
      fontSize: "0.85rem",
      fontWeight: "600",
      fontFamily: "inherit",
      cursor: "pointer",
    },

    // Status indicator (small badge)
    statusBadge: {
      position: "fixed",
      bottom: "100px",
      left: "16px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 12px",
      background: permission === "granted" ? c.greenLight : c.redLight,
      border: `1px solid ${permission === "granted" ? c.greenBorder : c.redBorder}`,
      borderRadius: "100px",
      fontSize: "0.7rem",
      fontWeight: "600",
      color: permission === "granted" ? c.green : c.red,
      zIndex: 9996,
      letterSpacing: "0.5px",
    },

    statusDot: {
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      background: permission === "granted" ? c.green : c.red,
    },
  };

  return (
    <>
      {/* ─── Permission Banner ─── */}
      <AnimatePresence>
        {showBanner && permission === "default" && (
          <motion.div
            style={styles.banner}
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div style={styles.bannerContent}>
              <div style={styles.bannerHeader}>
                <div style={styles.bannerIcon}>
                  <FiBell />
                </div>
                <div style={styles.bannerText}>
                  <p style={styles.bannerTitle}>Enable Notifications</p>
                  <p style={styles.bannerDesc}>
                    Stay updated with the latest posts, projects, and announcements.
                  </p>
                </div>
              </div>

              <div style={styles.bannerActions}>
                <motion.button
                  style={styles.allowBtn}
                  onClick={handleAllow}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FiBell /> Allow
                </motion.button>
                <motion.button
                  style={styles.dismissBtn}
                  onClick={handleDismiss}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FiX /> Later
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Status Badge ─── */}
      <AnimatePresence>
        {permission !== "default" && !dismissed && (
          <motion.div
            style={styles.statusBadge}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ delay: 0.5 }}
          >
            <div style={styles.statusDot} />
            {permission === "granted" ? "Notifications On" : "Notifications Off"}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PushNotification;
